import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { usePageMeta } from "@/hooks/usePageMeta";
import { rupee, STATUS_LABEL } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ShoppingCart, Loader2, Eye, MapPin, Phone } from "lucide-react";

const STATUSES = ["pending", "confirmed", "packed", "out_for_delivery", "delivered", "cancelled"];

export default function AdminOrders() {
  usePageMeta("Manage Orders");
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  const load = () => {
    setLoading(true);
    const params = filter !== "all" ? { status: filter } : {};
    api.get("/admin/orders", { params }).then(({ data }) => setOrders(data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, [filter]);

  const updateStatus = async (order, status) => {
    try {
      await api.put(`/admin/orders/${order.id}/status`, { status });
      toast.success(`Order ${order.order_number} → ${STATUS_LABEL[status]}`);
      setOrders((prev) => prev.map((o) => (o.id === order.id ? { ...o, status } : o)));
      if (detail?.id === order.id) setDetail({ ...detail, status });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const badge = (s) => s === "delivered" ? "bg-sage/15 text-sage" : s === "cancelled" ? "bg-destructive/10 text-destructive" : "bg-brand/10 text-brand";

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-ink sm:text-3xl">Orders</h1>
          <p className="mt-1 text-ink-soft">Track and update order statuses.</p>
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-48 rounded-full" data-testid="order-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All orders</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="mt-10 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>
      ) : orders.length === 0 ? (
        <div className="mt-10 rounded-2xl border border-dashed border-border bg-white p-12 text-center">
          <ShoppingCart className="mx-auto h-10 w-10 text-ink-muted" />
          <p className="mt-4 font-heading text-lg text-ink">No orders found</p>
        </div>
      ) : (
        <div className="mt-6 overflow-hidden rounded-2xl border border-border/60 bg-white shadow-ambient">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-cream-alt/60 text-xs uppercase tracking-wide text-ink-muted">
                <tr>
                  <th className="px-4 py-3">Order</th>
                  <th className="px-4 py-3">Customer</th>
                  <th className="px-4 py-3">Total</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Update</th>
                  <th className="px-4 py-3 text-right"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {orders.map((o) => (
                  <tr key={o.id} data-testid={`admin-order-${o.order_number}`}>
                    <td className="px-4 py-3">
                      <p className="font-medium text-ink">{o.order_number}</p>
                      <p className="text-xs text-ink-muted">{new Date(o.created_at).toLocaleDateString("en-IN")}</p>
                    </td>
                    <td className="px-4 py-3 text-ink-soft">{o.customer_name}<br /><span className="text-xs">{o.customer_mobile}</span></td>
                    <td className="px-4 py-3 font-semibold text-ink">{rupee(o.total)}</td>
                    <td className="px-4 py-3"><span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${badge(o.status)}`}>{STATUS_LABEL[o.status]}</span></td>
                    <td className="px-4 py-3">
                      <Select value={o.status} onValueChange={(v) => updateStatus(o, v)}>
                        <SelectTrigger className="w-40 rounded-lg" data-testid={`status-select-${o.order_number}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button variant="ghost" size="icon" onClick={() => setDetail(o)} data-testid={`view-order-${o.order_number}`}><Eye className="h-4 w-4" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          {detail && (
            <>
              <DialogHeader><DialogTitle className="font-heading">Order {detail.order_number}</DialogTitle></DialogHeader>
              <div className="space-y-4 text-sm">
                <div className="rounded-xl bg-cream-alt/50 p-4">
                  <p className="flex items-center gap-2 text-ink"><Phone className="h-4 w-4 text-brand" /> {detail.customer_name} · {detail.customer_mobile}</p>
                  <p className="mt-2 flex items-start gap-2 text-ink-soft"><MapPin className="mt-0.5 h-4 w-4 text-brand" /> {detail.address}</p>
                  {detail.delivery_location && <p className="mt-1 text-xs text-ink-muted">Area: {detail.delivery_location}</p>}
                  {detail.notes && <p className="mt-1 text-xs text-ink-muted">Note: {detail.notes}</p>}
                </div>
                <div>
                  <p className="font-semibold text-ink">Items</p>
                  <ul className="mt-2 space-y-1 text-ink-soft">
                    {detail.items.map((it, i) => <li key={i} className="flex justify-between"><span>{it.name} × {it.quantity}</span><span>{rupee(it.line_total)}</span></li>)}
                  </ul>
                </div>
                <div className="space-y-1 border-t border-border pt-3">
                  <div className="flex justify-between text-ink-soft"><span>Subtotal</span><span>{rupee(detail.subtotal)}</span></div>
                  {detail.discount > 0 && <div className="flex justify-between text-sage"><span>Discount {detail.coupon_code ? `(${detail.coupon_code})` : ""}</span><span>-{rupee(detail.discount)}</span></div>}
                  <div className="flex justify-between text-ink-soft"><span>Delivery</span><span>{detail.delivery_charge > 0 ? rupee(detail.delivery_charge) : "Free"}</span></div>
                  <div className="flex justify-between font-bold text-ink"><span>Total (COD)</span><span>{rupee(detail.total)}</span></div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
